import React, {
  createContext, useCallback, useContext,
  useEffect, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, CalendarCheck, BookOpen, FileText, Bell,
  GraduationCap, Award, ClipboardList, Clock, CalendarOff,
  Library, Building2, BookMarked, BarChart3, BarChart2, Bot,
  Settings, ChevronLeft, ChevronRight, LogOut, Menu,
  ChevronDown, Search, HelpCircle, Moon,
  Home, MessageSquare, BookMarked as BookMarkedAlt, Map, Download,
  Users, Contact, UserCircle, Megaphone,
  type LucideIcon,
} from 'lucide-react';
import { useLang } from '../lib/lang';
import { useTheme } from '../lib/theme';
import { clearTokens, primaryRoleLabel, type AuthUser } from '../lib/auth';
import { getNavCapabilities, primaryRole, schoolIdFromUser, ROLE_LABELS } from '../lib/rbac';
import { T, ROLE_PILL_COLORS } from '../lib/tokens';

// ── Shell context ─────────────────────────────────────────────────────────────

interface ShellCtx { setPageTitle:(t:string)=>void; collapsed:boolean; }
const ShellContext = createContext<ShellCtx>({ setPageTitle:()=>{}, collapsed:false });
export function useShell() { return useContext(ShellContext); }

// ── Nav definition ────────────────────────────────────────────────────────────

interface NavItem { path:string; labelMs:string; labelEn:string; Icon:LucideIcon; cap?:keyof ReturnType<typeof getNavCapabilities>; }
interface NavGroup { groupMs:string; groupEn:string; items:NavItem[]; cap?:keyof ReturnType<typeof getNavCapabilities>; }

const ALL_NAV: NavGroup[] = [
  { groupMs:'PAPAN PEMUKA', groupEn:'OVERVIEW', items:[
    { path:'/',                          labelMs:'Papan Pemuka',    labelEn:'Dashboard',       Icon:LayoutDashboard },
  ]},
  { groupMs:'PELAJAR', groupEn:'STUDENTS', cap:'showStudents', items:[
    { path:'/admin/attendance',          labelMs:'Kehadiran',       labelEn:'Attendance',      Icon:CalendarCheck },
    { path:'/admin/grades',              labelMs:'Gred & Markah',   labelEn:'Grades',          Icon:BookOpen },
    { path:'/admin/applications',        labelMs:'Permohonan',      labelEn:'Applications',    Icon:FileText,    cap:'showApplications' },
    { path:'/admin/attendance/alerts',   labelMs:'Makluman',        labelEn:'Alerts',          Icon:Bell,        cap:'showAlerts' },
  ]},
  { groupMs:'PENDIDIK', groupEn:'EDUCATORS', cap:'showEducators', items:[
    { path:'/admin/educators',           labelMs:'Senarai Pendidik',labelEn:'Educators',       Icon:GraduationCap },
    { path:'/admin/cpd',                 labelMs:'CPD',             labelEn:'CPD',             Icon:Award },
    { path:'/admin/tpa',                 labelMs:'TPA',             labelEn:'TPA',             Icon:ClipboardList },
    { path:'/admin/timetable',           labelMs:'Jadual Waktu',    labelEn:'Timetable',       Icon:Clock,       cap:'showTimetable' },
    { path:'/admin/timetable/substitute',labelMs:'Penggantian',     labelEn:'Substitution',    Icon:CalendarOff, cap:'showSubstitute' },
  ]},
  { groupMs:'PERPUSTAKAAN', groupEn:'LIBRARY', cap:'showLibrary', items:[
    { path:'/admin/library',             labelMs:'Katalog',         labelEn:'Catalogue',       Icon:Library },
    { path:'/admin/library/loans',       labelMs:'Pinjaman',        labelEn:'Loans',           Icon:BookMarked,  cap:'showLibraryAdmin' },
    { path:'/admin/library/reservations',labelMs:'Tempahan',        labelEn:'Reservations',    Icon:ClipboardList,cap:'showLibraryAdmin' },
  ]},
  { groupMs:'PENDIDIKAN SWASTA', groupEn:'PRIVATE EDUCATION', cap:'showDpe', items:[
    { path:'/admin/dpe',                 labelMs:'Sekolah Swasta',  labelEn:'Private Schools', Icon:Building2 },
    { path:'/admin/dpe/inspections',     labelMs:'Pemeriksaan',     labelEn:'Inspections',     Icon:ClipboardList },
    { path:'/admin/dpe/approvals',       labelMs:'Kelulusan Guru',  labelEn:'Approvals',       Icon:GraduationCap },
    { path:'/admin/dpe/bulletins',       labelMs:'Buletin',         labelEn:'Bulletins',       Icon:Bell },
  ]},
  { groupMs:'KEMENTERIAN AGAMA', groupEn:'RELIGIOUS AFFAIRS', cap:'showMora', items:[
    { path:'/admin/mora',                labelMs:'Sekolah MORA',    labelEn:'MORA Schools',    Icon:Moon },
  ]},
  { groupMs:'LAPORAN & ANALITIK', groupEn:'REPORTS', cap:'showReports', items:[
    { path:'/admin/reports',             labelMs:'Laporan',         labelEn:'Reports',         Icon:BarChart3 },
    { path:'/admin/moe-dashboard',       labelMs:'Papan Pemuka KPM',labelEn:'MOE Dashboard',  Icon:BarChart2,   cap:'showMoeDashboard' },
  ]},
  { groupMs:'SISTEM', groupEn:'SYSTEM', items:[
    { path:'/admin/edubot',              labelMs:'EduBot AI',       labelEn:'EduBot AI',       Icon:Bot },
    { path:'/admin/system-controls',     labelMs:'Kawalan Sistem',  labelEn:'System Controls', Icon:Settings,    cap:'showDemoControls' },
  ]},
];

// ── Parent-specific nav ───────────────────────────────────────────────────────

const PARENT_NAV: NavGroup[] = [
  { groupMs:'UTAMA', groupEn:'MAIN', items:[
    { path:'/',                         labelMs:'Papan Pemuka',     labelEn:'Dashboard',        Icon:LayoutDashboard },
  ]},
  { groupMs:'ANAK SAYA', groupEn:'MY CHILD', items:[
    { path:'/parent/kehadiran',         labelMs:'Kehadiran',        labelEn:'Attendance',       Icon:CalendarCheck },
    { path:'/parent/akademik',          labelMs:'Akademik',         labelEn:'Academic',         Icon:BookOpen },
    { path:'/parent/jadual',            labelMs:'Jadual Waktu',     labelEn:'Timetable',        Icon:Clock },
    { path:'/parent/laporan',           labelMs:'Laporan',          labelEn:'Reports',          Icon:Download },
    { path:'/parent/profil',            labelMs:'Profil Anak',      labelEn:'Child Profile',    Icon:UserCircle },
  ]},
  { groupMs:'SEKOLAH', groupEn:'SCHOOL', items:[
    { path:'/parent/pemberitahuan',     labelMs:'Pemberitahuan',    labelEn:'Notifications',    Icon:Bell },
    { path:'/parent/pekeliling',        labelMs:'Pekeliling',       labelEn:'Circulars',        Icon:Megaphone },
    { path:'/parent/komunikasi',        labelMs:'Komunikasi',       labelEn:'Communication',    Icon:MessageSquare },
    { path:'/parent/direktori',         labelMs:'Direktori Sekolah',labelEn:'School Directory', Icon:Users },
  ]},
];

// ── Colours ───────────────────────────────────────────────────────────────────

const S = {
  bg:       '#0F1117',
  surface:  '#1E2128',
  border:   'rgba(255,255,255,0.06)',
  yellow:   '#F7D000',
  text:     '#F9FAFB',
  muted:    '#6B7280',
  hover:    'rgba(255,255,255,0.04)',
  active:   'rgba(247,208,0,0.08)',
};

// ── Sidebar ───────────────────────────────────────────────────────────────────

function Sidebar({ user, onLogout, collapsed, onToggle, mobileOpen, onMobileClose }:{
  user:AuthUser; onLogout:()=>void; collapsed:boolean;
  onToggle:()=>void; mobileOpen:boolean; onMobileClose:()=>void;
}) {
  const { lang } = useLang();
  const location = useLocation();
  const navigate  = useNavigate();
  const caps = getNavCapabilities(user);
  const role = primaryRole(user);
  const rolePill = ROLE_PILL_COLORS[role];
  const schoolId = schoolIdFromUser(user);
  const isParent = role === 'parent';
  const activeNav = isParent ? PARENT_NAV : ALL_NAV;

  const isActive = (path:string) => path==='/' ? location.pathname==='/' : location.pathname.startsWith(path);

  function nav(path:string) { navigate(path); onMobileClose(); }

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          onClick={onMobileClose}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(2px)', zIndex:299 }}
        />
      )}

      <nav
        className="s-sidebar"
        aria-label={lang==='en'?'Main navigation':'Navigasi utama'}
        style={{
          position:'fixed', top:0, left:0, bottom:0,
          width: collapsed ? T.layout.sidebarCollapsed : T.layout.sidebarW,
          backgroundColor: S.bg,
          borderRight: `1px solid ${S.border}`,
          display:'flex', flexDirection:'column',
          transition:'width 0.2s ease',
          overflowX:'hidden', overflowY:'auto',
          zIndex:300,
          transform: mobileOpen ? 'translateX(0)' : undefined,
        }}
      >
        {/* ── Logo / brand ─────────────────────────────────────────────── */}
        <div style={{
          padding: collapsed ? '20px 0' : '20px 16px',
          display:'flex', alignItems:'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          borderBottom:`1px solid ${S.border}`,
          minHeight:'64px', flexShrink:0,
        }}>
          {!collapsed && (
            <div style={{ display:'flex', alignItems:'center', gap:'10px', minWidth:0 }}>
              {/* Brunei-inspired mark */}
              <div style={{
                width:'34px', height:'34px',
                background:`linear-gradient(135deg, ${S.yellow} 0%, #C9A800 100%)`,
                borderRadius:'8px',
                display:'flex', alignItems:'center', justifyContent:'center',
                flexShrink:0, boxShadow:'0 2px 8px rgba(247,208,0,0.3)',
              }}>
                <span style={{ fontFamily:T.font.serif, fontWeight:700, fontSize:'16px', color:'#0F1117', letterSpacing:'-0.02em' }}>S</span>
              </div>
              <div style={{ minWidth:0 }}>
                <div style={{ color:S.text, fontFamily:T.font.serif, fontWeight:700, fontSize:'15px', letterSpacing:'-0.01em', lineHeight:1.2 }}>
                  SERPS
                </div>
                <div style={{ color:'#4A5568', fontSize:'10px', lineHeight:1.3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {lang==='en' ? 'School Records System' : 'Sistem Rekod Sekolah'}
                </div>
              </div>
            </div>
          )}
          {collapsed && (
            <div style={{
              width:'34px', height:'34px',
              background:`linear-gradient(135deg, ${S.yellow} 0%, #C9A800 100%)`,
              borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center',
              boxShadow:'0 2px 8px rgba(247,208,0,0.3)',
            }}>
              <span style={{ fontFamily:T.font.serif, fontWeight:700, fontSize:'16px', color:'#0F1117' }}>S</span>
            </div>
          )}
          {!collapsed && (
            <button onClick={onToggle} aria-label="Runtuhkan / Collapse" style={iconBtnSt}>
              <ChevronLeft size={15} />
            </button>
          )}
        </div>

        {/* ── School context pill ───────────────────────────────────────── */}
        {!collapsed && schoolId && (
          <div style={{ padding:'10px 16px', borderBottom:`1px solid ${S.border}` }}>
            <div style={{
              display:'inline-flex', alignItems:'center', gap:'5px',
              background:'rgba(247,208,0,0.1)', border:'1px solid rgba(247,208,0,0.2)',
              borderRadius:'999px', padding:'4px 10px',
              fontSize:'11px', color:'#F7D000', fontWeight:500,
              maxWidth:'100%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
            }}>
              <span>📍</span>
              <span>SMAS Sayyidina Ali</span>
            </div>
          </div>
        )}

        {/* ── Nav groups ───────────────────────────────────────────────── */}
        <div style={{ flex:1, overflowY:'auto', padding:'8px 0 16px' }}>
          {activeNav.map((group) => {
            if (group.cap && !caps[group.cap]) return null;
            const items = group.items.filter((i) => !i.cap || caps[i.cap]);
            if (!items.length) return null;

            return (
              <div key={group.groupMs} style={{ marginBottom:'4px' }}>
                {!collapsed && (
                  <div style={{
                    padding:'10px 16px 4px',
                    fontSize:'10px', fontWeight:600,
                    letterSpacing:'0.1em', textTransform:'uppercase',
                    color:'#4A5568', userSelect:'none',
                    fontFamily:T.font.sans,
                  }}>
                    {lang==='en' ? group.groupEn : group.groupMs}
                  </div>
                )}
                {collapsed && <div style={{ height:'6px' }} />}
                {items.map((item) => {
                  const active = isActive(item.path);
                  return <NavBtn key={item.path} item={item} active={active} collapsed={collapsed} lang={lang} onClick={()=>nav(item.path)} />;
                })}
              </div>
            );
          })}
        </div>

        {/* ── User section ─────────────────────────────────────────────── */}
        <div style={{ borderTop:`1px solid ${S.border}`, padding: collapsed ? '12px 0' : '12px', flexShrink:0 }}>
          {collapsed ? (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'8px' }}>
              <Avatar email={user.email} size={32} />
              <button onClick={onToggle} aria-label="Kembangkan / Expand" style={iconBtnSt}>
                <ChevronRight size={15} />
              </button>
              <button onClick={onLogout} aria-label="Log keluar / Logout" style={{ ...iconBtnSt, color:'#ef4444' }}>
                <LogOut size={15} />
              </button>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {/* Role badge */}
              {rolePill && (
                <div style={{
                  display:'inline-flex', alignItems:'center',
                  background:rolePill.bg, color:rolePill.color,
                  borderRadius:'999px', padding:'3px 10px',
                  fontSize:'11px', fontWeight:600,
                  width:'fit-content', maxWidth:'100%',
                }}>
                  {lang==='en' ? rolePill.label_en : rolePill.label_ms}
                </div>
              )}
              {/* User row */}
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <Avatar email={user.email} size={32} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:S.text, fontSize:'13px', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {user.email.split('@')[0]}
                  </div>
                  <div style={{ fontSize:'11px', color:'#4A5568', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {user.email}
                  </div>
                </div>
                <button onClick={onLogout} aria-label="Log keluar" style={{ ...iconBtnSt, flexShrink:0 }}>
                  <LogOut size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

// ── Nav button ────────────────────────────────────────────────────────────────

function NavBtn({ item, active, collapsed, lang, onClick }:{
  item:NavItem; active:boolean; collapsed:boolean; lang:string; onClick:()=>void;
}) {
  const [hovered, setHovered] = useState(false);
  const label = lang==='en' ? item.labelEn : item.labelMs;
  return (
    <button
      onClick={onClick}
      onMouseEnter={()=>setHovered(true)}
      onMouseLeave={()=>setHovered(false)}
      title={collapsed ? label : undefined}
      aria-current={active?'page':undefined}
      style={{
        display:'flex', alignItems:'center',
        gap: collapsed ? 0 : '10px',
        width:'100%',
        padding: collapsed ? '10px 0' : '9px 16px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        background: active ? S.active : hovered ? S.hover : 'transparent',
        color: active ? S.yellow : hovered ? '#D1D5DB' : '#6B7280',
        border:'none',
        borderLeft: active ? `3px solid ${S.yellow}` : '3px solid transparent',
        cursor:'pointer',
        fontFamily: T.font.sans,
        fontSize:'13.5px',
        fontWeight: active ? 600 : 400,
        transition:'all 0.1s ease',
        textAlign:'left',
        outline:'none',
        letterSpacing:'0',
      }}
    >
      <item.Icon size={16} strokeWidth={active ? 2.2 : 1.8} style={{ flexShrink:0 }} />
      {!collapsed && (
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {label}
        </span>
      )}
    </button>
  );
}

// ── Top navbar ────────────────────────────────────────────────────────────────

function Navbar({ pageTitle, user, collapsed, onToggleMobile }:{
  pageTitle:string; user:AuthUser; collapsed:boolean; onToggleMobile:()=>void;
}) {
  const { lang, setLang } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const location = useLocation();
  const caps = getNavCapabilities(user);
  const role = primaryRole(user);
  const roleLabel = ROLE_LABELS[role];
  const schoolId  = schoolIdFromUser(user);
  const sidebarW  = collapsed ? T.layout.sidebarCollapsed : T.layout.sidebarW;

  const breadcrumb = buildBreadcrumb(location.pathname, lang);

  return (
    <header
      className="s-navbar"
      style={{
        position:'fixed', top:0, left:sidebarW, right:0,
        height:T.layout.navbarH,
        background:'var(--navbar-bg)',
        borderBottom:'1px solid var(--navbar-border)',
        display:'flex', alignItems:'center',
        padding:'0 24px', gap:'12px',
        zIndex:200, transition:'left 0.2s ease, background 200ms ease',
      }}
    >
      {/* Mobile hamburger */}
      <button
        onClick={onToggleMobile}
        aria-label={lang==='en'?'Open menu':'Buka menu'}
        className="mobile-only"
        style={{ ...iconBtnDark, display:'none' }}
      >
        <Menu size={20} />
      </button>

      {/* Breadcrumb */}
      <div style={{ flex:1, display:'flex', alignItems:'center', gap:'4px', minWidth:0 }}>
        <nav aria-label="Breadcrumb" style={{ display:'flex', alignItems:'center', gap:'4px', overflow:'hidden' }}>
          {breadcrumb.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ color:'#D1D5DB', fontSize:'13px', flexShrink:0 }}>/</span>}
              <span style={{
                fontSize:'13px',
                color: i === breadcrumb.length-1 ? '#111827' : '#6B7280',
                fontWeight: i === breadcrumb.length-1 ? 600 : 400,
                whiteSpace:'nowrap',
                overflow:'hidden', textOverflow:'ellipsis',
                maxWidth: i === breadcrumb.length-1 ? '200px' : '120px',
              }}>
                {crumb}
              </span>
            </React.Fragment>
          ))}
        </nav>

        {/* Read-only indicator */}
        {caps.isReadOnly && (
          <span
            title={lang==='en'?'View only — no edit permissions':'Paparan sahaja — tiada kebenaran mengedit'}
            style={{
              display:'inline-flex', alignItems:'center', gap:'3px',
              fontSize:'10px', fontWeight:600,
              color:'#6B7280', background:'#F3F4F6',
              border:'1px solid #E5E7EB', borderRadius:'999px',
              padding:'2px 8px', whiteSpace:'nowrap',
              cursor:'help', userSelect:'none', flexShrink:0,
              marginLeft:'6px',
            }}
          >
            🔒 {lang==='en'?'View only':'Paparan sahaja'}
          </span>
        )}
      </div>

      {/* School context — visible when scoped to a school */}
      {schoolId && !caps.showMoeDashboard && (
        <div style={{
          display:'flex', alignItems:'center', gap:'4px',
          fontSize:'11px', fontWeight:600,
          color:'#374151', background:'#FFFAED',
          border:'1px solid #F7D000', borderRadius:'999px',
          padding:'4px 12px', whiteSpace:'nowrap', flexShrink:0,
          cursor:'default',
        }}>
          📍 {lang==='en'?'SMAS Ali':'SMAS Sayyidina Ali'}
        </div>
      )}

      {/* Language toggle — underline style, not pill */}
      <div
        role="group" aria-label="Language / Bahasa"
        style={{ display:'flex', alignItems:'center', gap:'0', flexShrink:0 }}
      >
        {(['ms','en'] as const).map((l) => {
          const active = lang===l;
          return (
            <button
              key={l}
              onClick={()=>setLang(l)}
              aria-pressed={active}
              style={{
                padding:'4px 10px',
                background:'none', border:'none',
                borderBottom: active ? `2px solid #F7D000` : '2px solid transparent',
                color: active ? '#111827' : '#9CA3AF',
                fontSize:'12px', fontWeight: active ? 700 : 500,
                cursor:'pointer', transition:'all 0.1s',
                fontFamily: T.font.sans,
                letterSpacing:'0.04em',
                textTransform:'uppercase',
              }}
            >
              {l==='ms'?'BM':'EN'}
            </button>
          );
        })}
      </div>

      {/* Role badge */}
      <div
        title={`${lang==='en'?'Role:':'Peranan:'} ${roleLabel?.[lang==='en'?'en':'ms']??role}`}
        style={{
          display:'flex', alignItems:'center', gap:'4px',
          fontSize:'11px', fontWeight:700,
          color: ROLE_PILL_COLORS[role]?.color ?? '#111827',
          background: ROLE_PILL_COLORS[role]?.bg ?? '#F7D000',
          borderRadius:'999px', padding:'4px 12px',
          whiteSpace:'nowrap', flexShrink:0, cursor:'help',
        }}
      >
        {lang==='en' ? (ROLE_PILL_COLORS[role]?.label_en ?? role) : (ROLE_PILL_COLORS[role]?.label_ms ?? role)}
      </div>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        aria-label={theme === 'dark' ? (lang==='en'?'Switch to light mode':'Tukar ke mod cerah') : (lang==='en'?'Switch to dark mode':'Tukar ke mod gelap')}
        title={theme === 'dark' ? (lang==='en'?'Light mode':'Mod Cerah') : (lang==='en'?'Dark mode':'Mod Gelap')}
        className="theme-toggle"
      >
        <span className="theme-toggle-knob">
          {theme === 'dark' ? '🌙' : '☀️'}
        </span>
      </button>

      {/* Help */}
      <button
        aria-label={lang==='en'?'Help':'Bantuan'}
        style={iconBtnDark}
        title={lang==='en'?'Help & Documentation':'Bantuan & Dokumentasi'}
      >
        <HelpCircle size={18} />
      </button>

      {/* User avatar */}
      <Avatar email={user.email} size={34} />
    </header>
  );
}

// ── Breadcrumb builder ────────────────────────────────────────────────────────

const PATH_LABELS: Record<string, { ms:string; en:string }> = {
  'admin':               { ms:'Admin',              en:'Admin' },
  'parent':              { ms:'Portal Ibu Bapa',    en:'Parent Portal' },
  'attendance':          { ms:'Kehadiran',           en:'Attendance' },
  'alerts':              { ms:'Makluman',            en:'Alerts' },
  'grades':              { ms:'Gred',                en:'Grades' },
  'applications':        { ms:'Permohonan',          en:'Applications' },
  'educators':           { ms:'Pendidik',            en:'Educators' },
  'cpd':                 { ms:'CPD',                 en:'CPD' },
  'tpa':                 { ms:'TPA',                 en:'TPA' },
  'timetable':           { ms:'Jadual',              en:'Timetable' },
  'substitute':          { ms:'Penggantian',         en:'Substitution' },
  'library':             { ms:'Perpustakaan',        en:'Library' },
  'loans':               { ms:'Pinjaman',            en:'Loans' },
  'reservations':        { ms:'Tempahan',            en:'Reservations' },
  'dpe':                 { ms:'Pendidikan Swasta',   en:'Private Education' },
  'inspections':         { ms:'Pemeriksaan',         en:'Inspections' },
  'approvals':           { ms:'Kelulusan',           en:'Approvals' },
  'bulletins':           { ms:'Buletin',             en:'Bulletins' },
  'mora':                { ms:'MORA',                en:'MORA' },
  'reports':             { ms:'Laporan',             en:'Reports' },
  'moe-dashboard':       { ms:'Papan KPM',           en:'MOE Dashboard' },
  'edubot':              { ms:'EduBot AI',           en:'EduBot AI' },
  'system-controls':     { ms:'Kawalan Sistem',      en:'System Controls' },
  'kehadiran':           { ms:'Kehadiran',           en:'Attendance' },
  'akademik':            { ms:'Akademik',            en:'Academic' },
  'jadual':              { ms:'Jadual Waktu',        en:'Timetable' },
  'laporan':             { ms:'Laporan',             en:'Reports' },
  'pemberitahuan':       { ms:'Pemberitahuan',       en:'Notifications' },
  'pekeliling':          { ms:'Pekeliling',          en:'Circulars' },
  'komunikasi':          { ms:'Komunikasi',          en:'Communication' },
  'direktori':           { ms:'Direktori Sekolah',   en:'School Directory' },
  'profil':              { ms:'Profil Anak',         en:'Child Profile' },
};

function buildBreadcrumb(pathname:string, lang:string): string[] {
  const parts = pathname.split('/').filter(Boolean);
  const crumbs: string[] = [lang==='en'?'Home':'Laman Utama'];
  for (const p of parts) {
    const lbl = PATH_LABELS[p];
    if (lbl) crumbs.push(lang==='en' ? lbl.en : lbl.ms);
  }
  return crumbs;
}

// ── Avatar ────────────────────────────────────────────────────────────────────

const AVATAR_COLORS = ['#1D4ED8','#0D7A4E','#D97706','#7C3AED','#B91C1C','#0D9488','#D97706'];

function Avatar({ email, size }:{ email:string; size:number }) {
  const initials = email.slice(0,2).toUpperCase();
  const colorIdx = email.charCodeAt(0) % AVATAR_COLORS.length;
  const bg = AVATAR_COLORS[colorIdx] ?? '#1D4ED8';
  return (
    <div
      aria-hidden title={email}
      style={{
        width:`${size}px`, height:`${size}px`,
        borderRadius:'50%', background:bg,
        color:'#FFFFFF', display:'flex',
        alignItems:'center', justifyContent:'center',
        fontWeight:700, fontSize:`${Math.floor(size*0.36)}px`,
        flexShrink:0, userSelect:'none',
        fontFamily:T.font.sans,
        boxShadow:'0 0 0 2px #FFFFFF, 0 0 0 3px rgba(0,0,0,0.1)',
      }}
    >
      {initials}
    </div>
  );
}
export { Avatar };

// ── Shared button styles ──────────────────────────────────────────────────────

const iconBtnSt: React.CSSProperties = {
  background:'none', border:'none', cursor:'pointer',
  color:'#6B7280', display:'flex', alignItems:'center',
  justifyContent:'center', padding:'6px',
  borderRadius:'6px', transition:'all 0.1s',
};

const iconBtnDark: React.CSSProperties = {
  background:'none', border:'none', cursor:'pointer',
  color:'#9CA3AF', display:'flex', alignItems:'center',
  justifyContent:'center', padding:'6px',
  borderRadius:'6px', transition:'color 0.1s',
};

// ── AppShell ──────────────────────────────────────────────────────────────────

export function AppShell({ user, onLogout, children }:{
  user:AuthUser; onLogout:()=>void; children:React.ReactNode;
}) {
  const [collapsed, setCollapsed]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pageTitle, setPageTitle]   = useState('Papan Pemuka');

  const handleLogout = useCallback(() => { clearTokens(); onLogout(); }, [onLogout]);
  const sidebarW = collapsed ? T.layout.sidebarCollapsed : T.layout.sidebarW;

  return (
    <ShellContext.Provider value={{ setPageTitle, collapsed }}>
      <Sidebar
        user={user} onLogout={handleLogout}
        collapsed={collapsed} onToggle={()=>setCollapsed(c=>!c)}
        mobileOpen={mobileOpen} onMobileClose={()=>setMobileOpen(false)}
      />

      <Navbar
        pageTitle={pageTitle} user={user}
        collapsed={collapsed} onToggleMobile={()=>setMobileOpen(o=>!o)}
      />

      <main
        className="s-main"
        style={{
          marginLeft: sidebarW,
          paddingTop: T.layout.navbarH,
          minHeight: '100vh',
          backgroundColor: 'var(--color-canvas)',
          transition: 'margin-left 0.2s ease, background-color 200ms ease',
        }}
      >
        <div style={{ padding:`${T.sp['8']} ${T.layout.contentPad}`, maxWidth:T.layout.contentMax }}>
          {children}
        </div>
      </main>

      {/* Mobile: expand sidebar button in collapsed state */}
      {collapsed && (
        <button
          onClick={()=>setCollapsed(false)}
          aria-label="Expand sidebar"
          style={{
            display:'none', // shown by CSS on desktop collapsed
            position:'fixed', top:'70px', left:'52px',
            width:'24px', height:'24px',
            background:'#F7D000', border:'none',
            borderRadius:'999px', cursor:'pointer',
            alignItems:'center', justifyContent:'center',
            boxShadow:'0 2px 8px rgba(0,0,0,0.15)',
            zIndex:301,
          }}
        >
          <ChevronRight size={12} color="#0F1117" />
        </button>
      )}
    </ShellContext.Provider>
  );
}

// ── Page title hook ───────────────────────────────────────────────────────────

export function usePageTitle(title:string) {
  const { setPageTitle } = useShell();
  useEffect(() => {
    setPageTitle(title);
    document.title = `${title} — SERPS · Kementerian Pendidikan Brunei Darussalam`;
    return () => {
      setPageTitle('SERPS');
      document.title = 'SERPS — Kementerian Pendidikan Brunei Darussalam';
    };
  }, [title, setPageTitle]);
}
